import {
  GeckoComponentRect,
  GeckoMarkupLayer,
  GeckoMarkupRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'

import { NiiSourcesZoneParams } from './niiReportApi'

export type ZoneBounds = {
  x_start: number
  y_start: number
  x_end: number
  y_end: number
  unit: string
}

const ZONE_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6']

/**
 * Converts zones to temporary markup layers for AssetViz visualization.
 * These are never saved to Rolodex - purely for rendering.
 */
export function zonesToMarkupLayers(
  zones: ReadonlyArray<NiiSourcesZoneParams>,
  componentId: string,
  orgId: string,
  siteId: string,
  unitId: string
): GeckoMarkupLayer[] {
  return zones
    .filter((zone) => zone.bounds && zone.componentId === componentId)
    .map((zone, index) => {
      // Use zone's color if available, otherwise fallback to palette
      const color = zone.color || ZONE_COLORS[index % ZONE_COLORS.length]

      // Create temporary RolodexEntryJson for this zone
      const markupLayerJson: RolodexEntryJson = {
        id: `zone-${zone.id}`,
        type: RolodexEntryType.MARKUP_LAYER,
        data: {
          name: zone.title,
          color: color,
          category: 'user-added',
          geometry: {
            x_measurement_unit: zone.bounds!.unit,
            y_measurement_unit: zone.bounds!.unit,
          },
          primitives: {
            [zone.id]: {
              kind: 'rect',
              x_start: zone.bounds!.x_start,
              y_start: zone.bounds!.y_start,
              x_end: zone.bounds!.x_end,
              y_end: zone.bounds!.y_end,
            },
          },
          schema_version: 5,
        },
        tags: {
          component: componentId,
          organization: orgId,
          site: siteId,
          unit: unitId,
        },
        index: {},
        generation: 1,
        updated_at: new Date().toISOString(),
        access_restriction: null,
      }

      return GeckoMarkupLayer.CreateFromJSON(markupLayerJson)
    })
}

/**
 * Converts a markup rectangle drawn by the user into zone bounds
 */
export function markupRectToZoneBounds(primitive: GeckoMarkupRect): ZoneBounds {
  return {
    x_start: primitive.rect.x.start.num,
    y_start: primitive.rect.y.start.num,
    x_end: primitive.rect.x.end.num,
    y_end: primitive.rect.y.end.num,
    unit: primitive.rect.x.unit.id,
  }
}

/**
 * Auto-calculate zone area from bounds
 */
export function calculateZoneArea(rect: GeckoComponentRect): number {
  const width = Math.abs(rect.x.end.num - rect.x.start.num)
  const height = Math.abs(rect.y.end.num - rect.y.start.num)

  // Convert to square meters using GeckoMeasurement
  const widthMeasurement = GeckoMeasurement.CreateFromValue(width, rect.x.unit)
  const heightMeasurement = GeckoMeasurement.CreateFromValue(
    height,
    rect.y.unit
  )

  const widthMeters = widthMeasurement.toNumber(GeckoMeasurementUnit.Meters)
  const heightMeters = heightMeasurement.toNumber(GeckoMeasurementUnit.Meters)

  return widthMeters * heightMeters
}

/**
 * Create a temporary drawing layer for AssetViz CreateMarkupRect mode
 */
export function createDrawingLayer(
  componentId: string,
  orgId: string,
  siteId: string,
  unitId: string,
  unit: string
): GeckoMarkupLayer {
  const drawingLayerJson: RolodexEntryJson = {
    id: 'temp-drawing-layer',
    type: RolodexEntryType.MARKUP_LAYER,
    data: {
      name: 'Drawing Zone',
      color: '#3B82F6',
      category: 'user-added',
      geometry: {
        x_measurement_unit: unit,
        y_measurement_unit: unit,
      },
      primitives: {},
      schema_version: 5,
    },
    tags: {
      component: componentId,
      organization: orgId,
      site: siteId,
      unit: unitId,
    },
    index: {},
    generation: 1,
    updated_at: new Date().toISOString(),
    access_restriction: null,
  }

  return GeckoMarkupLayer.CreateFromJSON(drawingLayerJson)
}

/**
 * Find the markup layer for a specific zone ID
 */
export function findMarkupLayerForZone(
  zoneId: string,
  markupLayers: ReadonlyArray<GeckoMarkupLayer>
): GeckoMarkupLayer | undefined {
  return markupLayers.find((layer) => layer.id === `zone-${zoneId}`)
}
